import { verifyEvent } from 'nostr-tools/pure'
import { POLL_RESPONSE_KIND } from './nostr.js'

// Primal's public cache server. Votes cast in Primal's web app are sent
// only here, never to public relays, so Verdant asks the cache for each
// NIP-88 poll's kind 1018 responses. Every event is signature-checked
// before it is used, so the cache can't forge or alter votes; it can only
// omit them.
export const PRIMAL_CACHE_URL = 'wss://cache2.primal.net/v1'

const PAGE_LIMIT = 100
const MAX_PAGES = 10 // up to ~1,000 votes per poll per load
const CONCURRENCY = 4
const REQUEST_TIMEOUT_MS = 10000

// Fetch every poll's votes from the Primal cache over one WebSocket.
// Calls onVote(event) for each verified vote and onPollDone(pollId) once a
// poll has been fully read. Returns a cancel function.
export function fetchPrimalPollVotes(pollIds, { onVote, onPollDone } = {}) {
  if (!pollIds.length || typeof WebSocket === 'undefined') return () => {}

  let ws
  try { ws = new WebSocket(PRIMAL_CACHE_URL) } catch { return () => {} }

  const queue = pollIds.map((pollId) => ({ pollId, until: null, page: 0 }))
  const active = new Map() // subId -> { job, ids:Set, oldest, timer }
  const seen = new Set()
  let nextSub = 0
  let closed = false

  const close = () => {
    if (closed) return
    closed = true
    for (const a of active.values()) clearTimeout(a.timer)
    active.clear()
    try { ws.close() } catch { /* noop */ }
  }

  const finish = (subId) => {
    const a = active.get(subId)
    if (!a) return
    clearTimeout(a.timer)
    active.delete(subId)
    try { ws.send(JSON.stringify(['CLOSE', subId])) } catch { /* noop */ }
    const { job } = a
    // A full page with new votes may have more behind it.
    if (a.newCount > 0 && a.count >= PAGE_LIMIT && job.page + 1 < MAX_PAGES && a.oldest) {
      queue.unshift({ pollId: job.pollId, until: a.oldest, page: job.page + 1 })
    } else {
      onPollDone?.(job.pollId)
    }
    pump()
  }

  const pump = () => {
    if (closed) return
    while (active.size < CONCURRENCY && queue.length) {
      const job = queue.shift()
      const subId = `verdant_pv_${nextSub++}`
      const params = { event_id: job.pollId, limit: PAGE_LIMIT }
      if (job.until) params.until = job.until
      active.set(subId, {
        job, count: 0, newCount: 0, oldest: null,
        timer: setTimeout(() => finish(subId), REQUEST_TIMEOUT_MS),
      })
      ws.send(JSON.stringify(['REQ', subId, { cache: ['poll_votes', params] }]))
    }
    if (!active.size && !queue.length) close()
  }

  ws.onopen = pump
  ws.onerror = close
  ws.onclose = () => { closed = true }
  ws.onmessage = (msg) => {
    let data
    try { data = JSON.parse(msg.data) } catch { return }
    const [type, subId, event] = data
    const a = active.get(subId)
    if (!a) return
    if (type === 'EOSE') return finish(subId)
    if (type !== 'EVENT' || event?.kind !== POLL_RESPONSE_KIND) return
    // Only votes that actually point at the poll we asked about.
    if (!(event.tags || []).some((t) => t[0] === 'e' && t[1] === a.job.pollId)) return
    a.count += 1
    if (!a.oldest || event.created_at < a.oldest) a.oldest = event.created_at
    if (seen.has(event.id)) return
    seen.add(event.id)
    a.newCount += 1
    let valid = false
    try { valid = verifyEvent(event) } catch { valid = false }
    if (valid) onVote?.(event)
  }

  return close
}
